import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionResourceAssignment, 
  ConstructionDailyReport,
  ResourceStats, 
  ResourceFilters 
} from '../types/construction-resources.types';

export const ConstructionResourcesService = {
  /**
   * Danh sách phân bổ tài nguyên
   */
  async getAssignments(companyId: string, filters: ResourceFilters = {}): Promise<{ data: ConstructionResourceAssignment[], total: number }> {
    let query = supabase
      .from('construction_resource_assignments')
      .select(`
        *,
        team:teams(id, name),
        project:projects(id, name),
        zone:project_zones(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('resource_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.teamId) query = query.eq('team_id', filters.teamId);
    if (filters.search) {
      query = query.or(`resource_name.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionResourceAssignment[],
      total: count || 0
    };
  },

  /**
   * Danh sách báo cáo ngày
   */
  async getDailyReports(companyId: string, projectId?: string): Promise<ConstructionDailyReport[]> {
    let query = supabase
      .from('team_daily_reports')
      .select(`
        *,
        team:teams(id, name, code),
        submitter:user_profiles!submitted_by(id, full_name)
      `)
      .eq('company_id', companyId);

    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query.order('report_date', { ascending: false });

    if (error) throw error;
    return data as ConstructionDailyReport[];
  },

  /**
   * Thống kê tài nguyên
   */
  async getResourceStats(companyId: string, projectId?: string): Promise<ResourceStats> {
    let query = supabase.from('construction_resource_assignments').select('status_code, resource_type').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const { count: reportCount } = await supabase
      .from('team_daily_reports')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .eq('status_code', 'submitted');

    return {
      totalAssignments: data.length,
      activeEquipmentCount: data.filter(d => d.resource_type === 'equipment' && d.status_code === 'in_use').length,
      reservedMaterialCount: data.filter(d => d.resource_type === 'inventory' && d.status_code === 'reserved').length,
      pendingReports: reportCount || 0,
      conflictCount: 0, // Mocked
    };
  },

  /**
   * CRUD
   */
  async assignResource(data: Partial<ConstructionResourceAssignment>): Promise<ConstructionResourceAssignment> {
    const { data: result, error } = await supabase
      .from('construction_resource_assignments')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionResourceAssignment;
  },

  async updateAssignment(id: string, updates: Partial<ConstructionResourceAssignment>): Promise<void> {
    const { error } = await supabase
      .from('construction_resource_assignments')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};

